import { FC } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

interface Props {
  message: string;
  onRetry: () => void;
  retrying: boolean;
}

const PaymentGatewayError: FC<Props> = ({ message, onRetry, retrying }) => {
  const { t } = useTranslation();

  return (
    <div className="my-6 rounded-2xl border border-zinc-200 bg-gradient-to-b from-zinc-50/80 to-white px-8 py-12 flex flex-col items-center text-center">
      <div className="mb-5 rounded-full bg-destructive/10 ring-8 ring-destructive/5 p-3">
        <AlertCircle className="h-6 w-6 text-destructive" strokeWidth={2.25} />
      </div>

      <h4 className="font-semibold text-base text-foreground mb-1.5 tracking-tight">
        {t(
          "fe.pages.flow.steps.payment.gatewayErrorTitle",
          "Payment unavailable",
        )}
      </h4>

      <p className="text-sm text-muted-foreground max-w-sm leading-relaxed mb-5">
        {t(
          "fe.pages.flow.steps.payment.gatewayErrorDescription",
          "We couldn't prepare the payment form. Please try again — if the problem persists, contact support.",
        )}
      </p>

      <Button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="cursor-pointer min-w-32"
      >
        <RefreshCw
          className={`h-4 w-4 mr-1.5 ${retrying ? "animate-spin" : ""}`}
        />
        {retrying
          ? t("ui.loading")
          : t("fe.pages.flow.steps.payment.gatewayErrorRetry", "Try again")}
      </Button>

      <p className="mt-6 font-mono text-[11px] text-muted-foreground/70 break-words max-w-md">
        {message}
      </p>
    </div>
  );
};

export default PaymentGatewayError;
